import { useEffect } from 'react';
import { DiagnosticsPanel } from '@common/components/diagnostics-panel';
import { DiagnosticsSchedulingSection } from './diagnostics-scheduling-section';
import { useRouter } from '@common-reducers/hooks/use-router';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserId } from '@common-reducers/UserSelectors';
import AppView from '@common/components/app-view';
import { isExtensionContext } from '@common/services/detect-context';

interface DiagnosticsViewProps {
  /** Optional userId prop - if provided, overrides the URL search param */
  supportedUserId?: string;
}

export default function DiagnosticsView({ supportedUserId }: DiagnosticsViewProps = {}) {
  // Hide Crisp chat widget on the diagnostics page
  useEffect(() => {
    if (typeof window !== 'undefined' && window.$crisp) {
      window.$crisp.push(["do", "chat:hide"]);
    }
    return () => {
      if (typeof window !== 'undefined' && window.$crisp) {
        window.$crisp.push(["do", "chat:show"]);
      }
    };
  }, []);

  // Use the context-aware router hook instead of react-router-dom directly
  const router = useRouter();

  // Get the authenticated user's ID as fallback
  const authenticatedUserId = useAppSelector(selectUserId);

  // Check if userId was explicitly provided via prop or URL param
  const urlSupportedUserId = router.query?.supportedUserId as string;
  const explicitUserId = supportedUserId || urlSupportedUserId;

  // Use explicit userId if provided, otherwise fall back to authenticated user
  const userId = explicitUserId || authenticatedUserId || '';

  if (!userId) {
    return (
      <AppView title="Diagnostics" showHeader={!isExtensionContext()}>
        <div className="max-w-3xl mx-auto py-8 px-4">
          <h2 className="text-lg font-semibold text-red-600">
            Not authenticated. Please log in to use diagnostics.
          </h2>
        </div>
      </AppView>
    );
  }

  // Check if running on own account or supporting another user
  const isSupportMode = explicitUserId && explicitUserId !== authenticatedUserId;

  return (
    <AppView title="WA Communication Layer Diagnostics" showHeader={!isExtensionContext()}>
      <div className="max-w-[1400px] mx-auto py-6 px-4 space-y-6">
        <p className="text-sm text-slate-500 mb-4">
          User: {userId}
          {isSupportMode && <span className="ml-2 text-orange-600">(Support Mode)</span>}
        </p>

        <div className="min-h-[80vh] space-y-6">
          {/* supportedUserId is only passed when explicitly targeting another user via URL param or prop.
              When not provided, the panel uses the authenticated user for engine filtering,
              and the backend uses the authenticated user from JWT for tool execution. */}
          <DiagnosticsPanel supportedUserId={explicitUserId} />
          <DiagnosticsSchedulingSection userId={userId} />
        </div>
      </div>
    </AppView>
  );
}
